import type { Language, Locale } from '@/config/locales';
import type { EmailSendTranslations } from './types';

// #522 — Locale 단일 출처 정합 (15언어 전수).
export const translations:
  & Record<Language, EmailSendTranslations>
  & Record<Exclude<Locale, Language>, EmailSendTranslations> = {
  ko: {
    title: '이메일로 전송',
    selectOption: '전송 방법을 선택하세요',
    useRegisteredEmail: '등록된 이메일로 전송',
    useNewEmail: '새 이메일로 전송',
    emailPlaceholder: '이메일 주소를 입력하세요',
    send: '전송',
    cancel: '취소',
    sending: '전송 중...',
    success: '이메일이 성공적으로 전송되었습니다!',
    error: '이메일 전송에 실패했습니다. 다시 시도해주세요.',
    invalidEmail: '유효한 이메일 주소를 입력하세요',
    noRegisteredEmail: '등록된 이메일이 없습니다',
    filesIncluded: 'PDF 및 XML 파일이 첨부됩니다'
  },
  es: {
    title: 'Enviar por correo',
    selectOption: 'Seleccione el método de envío',
    useRegisteredEmail: 'Enviar a correo registrado',
    useNewEmail: 'Enviar a nuevo correo',
    emailPlaceholder: 'Ingrese la dirección de correo',
    send: 'Enviar',
    cancel: 'Cancelar',
    sending: 'Enviando...',
    success: '¡Correo enviado exitosamente!',
    error: 'Error al enviar el correo. Inténtelo de nuevo.',
    invalidEmail: 'Ingrese una dirección de correo válida',
    noRegisteredEmail: 'No hay correo registrado',
    filesIncluded: 'Se adjuntarán archivos PDF y XML'
  },
  en: {
    title: 'Send via Email',
    selectOption: 'Select sending method',
    useRegisteredEmail: 'Send to registered email',
    useNewEmail: 'Send to new email',
    emailPlaceholder: 'Enter email address',
    send: 'Send',
    cancel: 'Cancel',
    sending: 'Sending...',
    success: 'Email sent successfully!',
    error: 'Failed to send email. Please try again.',
    invalidEmail: 'Please enter a valid email address',
    noRegisteredEmail: 'No registered email',
    filesIncluded: 'PDF and XML files will be attached'
  },
  pt: {
    title: 'Enviar por e-mail',
    selectOption: 'Selecione o método de envio',
    useRegisteredEmail: 'Enviar para o e-mail cadastrado',
    useNewEmail: 'Enviar para um novo e-mail',
    emailPlaceholder: 'Digite o endereço de e-mail',
    send: 'Enviar',
    cancel: 'Cancelar',
    sending: 'Enviando...',
    success: 'E-mail enviado com sucesso!',
    error: 'Falha ao enviar o e-mail. Tente novamente.',
    invalidEmail: 'Digite um endereço de e-mail válido',
    noRegisteredEmail: 'Nenhum e-mail cadastrado',
    filesIncluded: 'Os arquivos PDF e XML serão anexados'
  },
  fr: {
    title: 'Envoyer par e-mail',
    selectOption: 'Sélectionnez le mode d’envoi',
    useRegisteredEmail: 'Envoyer à l’e-mail enregistré',
    useNewEmail: 'Envoyer à un nouvel e-mail',
    emailPlaceholder: 'Saisissez l’adresse e-mail',
    send: 'Envoyer',
    cancel: 'Annuler',
    sending: 'Envoi en cours...',
    success: 'E-mail envoyé avec succès !',
    error: 'Échec de l’envoi de l’e-mail. Veuillez réessayer.',
    invalidEmail: 'Veuillez saisir une adresse e-mail valide',
    noRegisteredEmail: 'Aucun e-mail enregistré',
    filesIncluded: 'Les fichiers PDF et XML seront joints'
  },
  zh: {
    title: '通过电子邮件发送',
    selectOption: '请选择发送方式',
    useRegisteredEmail: '发送至已注册邮箱',
    useNewEmail: '发送至新邮箱',
    emailPlaceholder: '请输入电子邮箱地址',
    send: '发送',
    cancel: '取消',
    sending: '发送中...',
    success: '邮件发送成功！',
    error: '邮件发送失败，请重试。',
    invalidEmail: '请输入有效的电子邮箱地址',
    noRegisteredEmail: '没有已注册的邮箱',
    filesIncluded: '将附上 PDF 和 XML 文件'
  },
  ja: {
    title: 'メールで送信',
    selectOption: '送信方法を選択してください',
    useRegisteredEmail: '登録済みメールに送信',
    useNewEmail: '新しいメールに送信',
    emailPlaceholder: 'メールアドレスを入力してください',
    send: '送信',
    cancel: 'キャンセル',
    sending: '送信中...',
    success: 'メールが正常に送信されました！',
    error: 'メールの送信に失敗しました。もう一度お試しください。',
    invalidEmail: '有効なメールアドレスを入力してください',
    noRegisteredEmail: '登録済みのメールがありません',
    filesIncluded: 'PDFおよびXMLファイルが添付されます'
  },
  hi: {
    title: 'ईमेल से भेजें',
    selectOption: 'भेजने का तरीका चुनें',
    useRegisteredEmail: 'पंजीकृत ईमेल पर भेजें',
    useNewEmail: 'नए ईमेल पर भेजें',
    emailPlaceholder: 'ईमेल पता दर्ज करें',
    send: 'भेजें',
    cancel: 'रद्द करें',
    sending: 'भेजा जा रहा है...',
    success: 'ईमेल सफलतापूर्वक भेजा गया!',
    error: 'ईमेल भेजने में विफल। कृपया पुनः प्रयास करें।',
    invalidEmail: 'कृपया मान्य ईमेल पता दर्ज करें',
    noRegisteredEmail: 'कोई पंजीकृत ईमेल नहीं है',
    filesIncluded: 'PDF और XML फ़ाइलें संलग्न की जाएंगी'
  },
  de: {
    title: 'Per E-Mail senden',
    selectOption: 'Versandmethode auswählen',
    useRegisteredEmail: 'An registrierte E-Mail senden',
    useNewEmail: 'An neue E-Mail senden',
    emailPlaceholder: 'E-Mail-Adresse eingeben',
    send: 'Senden',
    cancel: 'Abbrechen',
    sending: 'Wird gesendet...',
    success: 'E-Mail erfolgreich gesendet!',
    error: 'E-Mail konnte nicht gesendet werden. Bitte versuchen Sie es erneut.',
    invalidEmail: 'Bitte geben Sie eine gültige E-Mail-Adresse ein',
    noRegisteredEmail: 'Keine registrierte E-Mail',
    filesIncluded: 'PDF- und XML-Dateien werden angehängt'
  },
  it: {
    title: 'Invia via e-mail',
    selectOption: 'Seleziona il metodo di invio',
    useRegisteredEmail: 'Invia all’e-mail registrata',
    useNewEmail: 'Invia a una nuova e-mail',
    emailPlaceholder: 'Inserisci l’indirizzo e-mail',
    send: 'Invia',
    cancel: 'Annulla',
    sending: 'Invio in corso...',
    success: 'E-mail inviata con successo!',
    error: 'Invio dell’e-mail non riuscito. Riprova.',
    invalidEmail: 'Inserisci un indirizzo e-mail valido',
    noRegisteredEmail: 'Nessuna e-mail registrata',
    filesIncluded: 'Verranno allegati i file PDF e XML'
  },
  ru: {
    title: 'Отправить по эл. почте',
    selectOption: 'Выберите способ отправки',
    useRegisteredEmail: 'Отправить на зарегистрированную почту',
    useNewEmail: 'Отправить на новую почту',
    emailPlaceholder: 'Введите адрес эл. почты',
    send: 'Отправить',
    cancel: 'Отмена',
    sending: 'Отправка...',
    success: 'Письмо успешно отправлено!',
    error: 'Не удалось отправить письмо. Попробуйте ещё раз.',
    invalidEmail: 'Введите действительный адрес эл. почты',
    noRegisteredEmail: 'Нет зарегистрированной почты',
    filesIncluded: 'Будут приложены файлы PDF и XML'
  },
  ar: {
    title: 'إرسال عبر البريد الإلكتروني',
    selectOption: 'اختر طريقة الإرسال',
    useRegisteredEmail: 'إرسال إلى البريد المسجل',
    useNewEmail: 'إرسال إلى بريد جديد',
    emailPlaceholder: 'أدخل عنوان البريد الإلكتروني',
    send: 'إرسال',
    cancel: 'إلغاء',
    sending: 'جارٍ الإرسال...',
    success: 'تم إرسال البريد الإلكتروني بنجاح!',
    error: 'فشل إرسال البريد الإلكتروني. يرجى المحاولة مرة أخرى.',
    invalidEmail: 'يرجى إدخال عنوان بريد إلكتروني صالح',
    noRegisteredEmail: 'لا يوجد بريد إلكتروني مسجل',
    filesIncluded: 'سيتم إرفاق ملفات PDF و XML'
  },
  vi: {
    title: 'Gửi qua email',
    selectOption: 'Chọn phương thức gửi',
    useRegisteredEmail: 'Gửi đến email đã đăng ký',
    useNewEmail: 'Gửi đến email mới',
    emailPlaceholder: 'Nhập địa chỉ email',
    send: 'Gửi',
    cancel: 'Hủy',
    sending: 'Đang gửi...',
    success: 'Email đã được gửi thành công!',
    error: 'Gửi email thất bại. Vui lòng thử lại.',
    invalidEmail: 'Vui lòng nhập địa chỉ email hợp lệ',
    noRegisteredEmail: 'Không có email đã đăng ký',
    filesIncluded: 'Tệp PDF và XML sẽ được đính kèm'
  },
  th: {
    title: 'ส่งทางอีเมล',
    selectOption: 'เลือกวิธีการส่ง',
    useRegisteredEmail: 'ส่งไปยังอีเมลที่ลงทะเบียน',
    useNewEmail: 'ส่งไปยังอีเมลใหม่',
    emailPlaceholder: 'กรอกที่อยู่อีเมล',
    send: 'ส่ง',
    cancel: 'ยกเลิก',
    sending: 'กำลังส่ง...',
    success: 'ส่งอีเมลสำเร็จแล้ว!',
    error: 'ส่งอีเมลไม่สำเร็จ กรุณาลองอีกครั้ง',
    invalidEmail: 'กรุณากรอกที่อยู่อีเมลที่ถูกต้อง',
    noRegisteredEmail: 'ไม่มีอีเมลที่ลงทะเบียน',
    filesIncluded: 'จะแนบไฟล์ PDF และ XML'
  },
  nl: {
    title: 'Verzenden via e-mail',
    selectOption: 'Selecteer de verzendmethode',
    useRegisteredEmail: 'Verzenden naar geregistreerde e-mail',
    useNewEmail: 'Verzenden naar nieuwe e-mail',
    emailPlaceholder: 'Voer het e-mailadres in',
    send: 'Verzenden',
    cancel: 'Annuleren',
    sending: 'Verzenden...',
    success: 'E-mail succesvol verzonden!',
    error: 'E-mail verzenden mislukt. Probeer het opnieuw.',
    invalidEmail: 'Voer een geldig e-mailadres in',
    noRegisteredEmail: 'Geen geregistreerde e-mail',
    filesIncluded: 'PDF- en XML-bestanden worden bijgevoegd'
  }
};

export function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}
